import { useState, useEffect } from 'react'
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Avatar,
    LinearProgress
} from '@mui/material'
import {
    ShoppingCart as OrdersIcon,
    People as UsersIcon,
    Inventory as ProductsIcon,
    AttachMoney as RevenueIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon
} from '@mui/icons-material'
import { collection, query, orderBy, limit, onSnapshot, getDocs } from 'firebase/firestore'
import { getFirebaseDb } from '../../config/firebase'
import { formatCurrency, formatDate, getOrderStatusColor } from '../../utils'

interface StatCardProps {
    title: string
    value: string | number
    change?: number
    icon: React.ReactNode
    color: string
}

function StatCard({ title, value, change, icon, color }: StatCardProps) {
    return (
        <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                        <Typography variant="body2" sx={{ color: 'grey.500', mb: 1 }}>
                            {title}
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                            {value}
                        </Typography>
                        {change !== undefined && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {change >= 0 ? (
                                    <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                ) : (
                                    <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
                                )}
                                <Typography
                                    variant="caption"
                                    sx={{ color: change >= 0 ? 'success.main' : 'error.main' }}
                                >
                                    {change >= 0 ? '+' : ''}{change}% from last month
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    <Box
                        sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            backgroundColor: `${color}20`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Box sx={{ color }}>{icon}</Box>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    )
}

export default function Dashboard() {
    const [stats, setStats] = useState({
        totalOrders: 0,
        totalUsers: 0,
        totalProducts: 0,
        totalRevenue: 0,
    })
    const [recentOrders, setRecentOrders] = useState<any[]>([])
    const [loading, setLoading] = useState(true)

    const db = getFirebaseDb()

    useEffect(() => {
        // Fetch stats
        const fetchStats = async () => {
            try {
                const ordersSnap = await getDocs(collection(db, 'orders'))
                const usersSnap = await getDocs(collection(db, 'users'))
                const productsSnap = await getDocs(collection(db, 'products'))

                let revenue = 0
                ordersSnap.docs.forEach(doc => {
                    const data = doc.data()
                    if (data.pricing?.total) {
                        revenue += data.pricing.total
                    }
                })

                setStats({
                    totalOrders: ordersSnap.size,
                    totalUsers: usersSnap.size,
                    totalProducts: productsSnap.size,
                    totalRevenue: revenue,
                })
            } catch (error) {
                console.error('Error fetching stats:', error)
            }
        }

        fetchStats()

        // Subscribe to recent orders
        const ordersQuery = query(
            collection(db, 'orders'),
            orderBy('createdAt', 'desc'),
            limit(10)
        )

        const unsubscribe = onSnapshot(ordersQuery, (snapshot) => {
            const orders = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }))
            setRecentOrders(orders)
            setLoading(false)
        })

        return () => unsubscribe()
    }, [db])

    return (
        <Box>
            {/* Stats Grid */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                        title="Total Orders"
                        value={stats.totalOrders}
                        change={12}
                        icon={<OrdersIcon sx={{ fontSize: 28 }} />}
                        color="#6366f1"
                    />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                        title="Total Users"
                        value={stats.totalUsers}
                        change={8}
                        icon={<UsersIcon sx={{ fontSize: 28 }} />}
                        color="#10b981"
                    />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                        title="Total Products"
                        value={stats.totalProducts}
                        change={5}
                        icon={<ProductsIcon sx={{ fontSize: 28 }} />}
                        color="#f59e0b"
                    />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                        title="Total Revenue"
                        value={formatCurrency(stats.totalRevenue)}
                        change={15}
                        icon={<RevenueIcon sx={{ fontSize: 28 }} />}
                        color="#ec4899"
                    />
                </Grid>
            </Grid>

            {/* Recent Orders */}
            <Card>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                        Recent Orders
                    </Typography>

                    {loading ? (
                        <LinearProgress />
                    ) : recentOrders.length === 0 ? (
                        <Typography variant="body2" sx={{ color: 'grey.500', textAlign: 'center', py: 4 }}>
                            No orders yet
                        </Typography>
                    ) : (
                        <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Order ID</TableCell>
                                        <TableCell>Customer</TableCell>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Total</TableCell>
                                        <TableCell>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {recentOrders.map((order) => (
                                        <TableRow key={order.id} hover>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    #{order.invoiceNumber || order.id.slice(0, 8)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                                                        {order.guestInfo?.name?.charAt(0) || 'G'}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {order.guestInfo?.name || 'Guest User'}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: 'grey.500' }}>
                                                            {order.guestInfo?.email || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {formatDate(order.createdAt)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {formatCurrency(order.pricing?.total || 0)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={order.status}
                                                    color={getOrderStatusColor(order.status)}
                                                    size="small"
                                                    sx={{ textTransform: 'capitalize' }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>
        </Box>
    )
}
